import { useGLTF } from '@react-three/drei';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import { useContext } from 'react';

import dc_blue from '@/assets/models/blue_donation_cup.glb';
import dc_green from '@/assets/models/green_donation_cup.glb';
import dc_red from '@/assets/models/red_donation_cup.glb';
import dc_yellow from '@/assets/models/yellow_donation_cup.glb';
import Sign3D from '@/components/game/3d/object/button/Sign3D';
import PhysicsWall from '@/components/game/3d/physics/PhysicsWall';
import { GameContext } from '@/state/contexts';

function PaymentAreaCollider({ onEnergyEnter, pos, showMesh }) {
	// Rapier args are half-extents (divide your original dimensions by 2)
	const hx = 8.75 / 2; //
	const hy = 2;
	const hz = 2.75 / 2;

	const pos2 = [pos[0], pos[1] - 2, pos[2]];

	return (
		// type="fixed" means it won't fall or move due to physics gravity
		<RigidBody position={pos2} type="fixed">
			<CuboidCollider
				args={[hx, hy, hz]}
				sensor // Crucial: acts as a trigger area, items pass right through
				onIntersectionEnter={(event) => {
					// event.other contains the object hitting the trigger
					const incomingNode = event.other.rigidBodyObject;

					if (incomingNode?.userData?.isEnergy && onEnergyEnter) {
						onEnergyEnter(incomingNode.userData);
					}
				}}
			/>
			{showMesh && (
				<mesh>
					<boxGeometry args={[hx * 2, hy * 2, hz * 2]} />
					<meshBasicMaterial color="red" wireframe />
				</mesh>
			)}
		</RigidBody>
	);
}

// TODO scale donation box over the top of the playermat area, flatten height
// match payment reception area to box

// TODO plane backboard to catch thrown energies

function PaymentAreaBounded({
	paymentAreaRef,
	worldBoxRef,
	playerId,
	matColor,
	matPathId,
	onEnergyEnter,
}) {
	let path = [dc_yellow, dc_blue, dc_red, dc_green];

	const pc = useContext(GameContext);

	// skip if it's not my turn or it's not my playermat
	if (
		pc.activePlayerId !== pc.currPlayer.playerId ||
		pc.activePlayerId !== playerId
	) {
		return;
	}

	const { nodes, materials } = useGLTF(path[matPathId]);

	const scale = [0.36, 0.3, 1];
	const payment_area = [0, 7, 9];

	const cup_h = 3.75;
	const cup_x = 6;
	const cup_z = 2;
	const cup_thicc = 0.25;

	const showCup = true;
	const showCollider = true;
	const showWalls = true;
	const showMesh = false;

	return (
		<group dispose={null}>
			{showCup && (
				<mesh
					castShadow
					receiveShadow
					geometry={nodes.lyoc_donation_cup.geometry}
					material={materials['Material.001']}
					position={payment_area}
					rotation={[0, Math.PI / 2, 0]}
					scale={scale}
				/>
			)}

			<Sign3D playerId={playerId} />

			{showCollider && (
				<PaymentAreaCollider
					paymentAreaRef={paymentAreaRef}
					worldBoxRef={worldBoxRef}
					onEnergyEnter={onEnergyEnter}
					pos={payment_area}
					showMesh={showMesh}
				/>
			)}

			{showWalls && (
				<>
					<PhysicsWall
						size={[cup_x, 0.5, 1.5]}
						pos={[
							payment_area[0],
							payment_area[1] - 1.9,
							payment_area[2],
						]}
						showMesh={showMesh}
					/>
					<PhysicsWall
						size={[cup_thicc, cup_h, cup_z]}
						pos={[
							payment_area[0] + 4.75,
							payment_area[1] - 2,
							payment_area[2],
						]}
						showMesh={showMesh}
					/>
					<PhysicsWall
						size={[cup_thicc, cup_h, cup_z]}
						pos={[
							payment_area[0] - 4.75,
							payment_area[1] - 2,
							payment_area[2],
						]}
						showMesh={showMesh}
					/>
					<PhysicsWall
						size={[cup_x, cup_h + 4, cup_thicc]}
						pos={[
							payment_area[0],
							payment_area[1] - 2,
							payment_area[2] - 1.85,
						]}
						showMesh={showMesh}
					/>
					<PhysicsWall
						size={[cup_x, cup_h, cup_thicc]}
						pos={[
							payment_area[0],
							payment_area[1] - 2,
							payment_area[2] + 1.85,
						]}
						showMesh={showMesh}
					/>
				</>
			)}
		</group>
	);
}

useGLTF.preload(dc_blue);

export default PaymentAreaBounded;
